import { CircleAlert, Icon } from "@norican/icons";
import { Alert, Button, cn, EmptyState, Typography } from "@norican/react";
import type { TOptions } from "i18next";
import { type ComponentPropsWithoutRef, type ReactNode, useCallback } from "react";

import { useT } from "@/i18n";

import {
  isRequesterUnresolvedError,
  loggedRef,
  normaliseApiError,
  shortRef,
  shouldOfferHelpdeskContact,
} from "./api-error";
import { HelpdeskContactLine } from "./helpdesk-contact-line";
import { RequesterUnresolvedNotice } from "./requester-unresolved-notice";

/**
 * Turns an unknown rejection into user-facing copy in the active language.
 *
 * Resolution order: a failure the server logged for IT (reassurance and a
 * short reference, never the reason — 8 Oct) → the network message when the
 * request never reached the server → the stable `code` from the envelope → the
 * generic apology. The server's own English `message` is never rendered; it is
 * developer text by contract.
 */
export interface ResolvedApiError {
  title: string;
  description: string;
  traceId: string | null;
  /** Pre-formatted "Reference: …" line, or `null` when there is no trace id. */
  traceLabel: string | null;
  /** True when the server logged this failure for IT (the reassuring copy). */
  logged: boolean;
}

export function useApiErrorMessage() {
  const { t, i18n } = useT("errors");

  return useCallback(
    (error: unknown): ResolvedApiError => {
      const normalised = normaliseApiError(error);
      const ref = shortRef(normalised.traceId);
      const traceLabel = ref === null ? null : t("traceId", { traceId: ref });

      const logged = loggedRef(error);
      if (logged !== null) {
        return {
          title: t("logged.title"),
          description: t("logged.description"),
          traceId: normalised.traceId,
          traceLabel: logged === "" ? null : t("logged.ref", { ref: logged }),
          logged: true,
        };
      }

      if (normalised.status === 0) {
        return {
          title: t("network.title"),
          description: t("network.description"),
          traceId: normalised.traceId,
          traceLabel,
          logged: false,
        };
      }

      if (normalised.code !== null && i18n.exists(`errors:code.${normalised.code}`)) {
        return {
          title: t("title"),
          // The key is checked with `i18n.exists` on the line above; the cast
          // only tells TypeScript that a runtime-built key is one of the union.
          // `params` fills a code's placeholders (e.g. the ticket number); a code
          // without any ignores them.
          description: t(
            `code.${normalised.code}` as "code.UNKNOWN",
            normalised.params as Omit<TOptions, "context">,
          ),
          traceId: normalised.traceId,
          traceLabel,
          logged: false,
        };
      }

      return {
        title: t("title"),
        description: t("description"),
        traceId: normalised.traceId,
        traceLabel,
        logged: false,
      };
    },
    [i18n, t],
  );
}

export interface ErrorStateProps extends Omit<ComponentPropsWithoutRef<"div">, "title"> {
  /** The rejection from a query or mutation. Translated via its error code. */
  error?: unknown;
  /** Overrides the derived headline. */
  title?: ReactNode;
  /** Overrides the derived explanation. */
  description?: ReactNode;
  /** When given, a "Try again" button is rendered. */
  onRetry?: () => void;
  /** Extra buttons beside the retry action. */
  actions?: ReactNode;
  /**
   * `block` fills the space a failed page or list would have taken;
   * `inline` is a compact banner for a form or a section that partly loaded.
   */
  variant?: "block" | "inline";
  /**
   * Set on write failures (ticket create, request submit, comment). When the
   * error is one a retry may not fix — `PROVIDER_UNAVAILABLE`,
   * `VALIDATION_FAILED`, a timeout, a dropped connection — the Global IT
   * Helpdesk contact line is shown after the "try again" copy, so the person
   * is never left with nowhere to go.
   */
  offerHelpdeskContact?: boolean;
}

/**
 * The standard "this didn't load" state.
 *
 * `inline` uses the design system `Alert`, which already carries
 * `role="alert"` for the danger tone, so a failure that appears after the page
 * has rendered is announced without any extra live region.
 */
export function ErrorState({
  error,
  title,
  description,
  onRetry,
  actions,
  variant = "block",
  offerHelpdeskContact = false,
  className,
  ...props
}: ErrorStateProps) {
  const { t } = useT();
  const resolve = useApiErrorMessage();
  const resolved = resolve(error);

  // "Ivanti has no record of you" is not a failure to retry: nothing on this
  // screen can fix it, so every error surface says who can, in one wording.
  // An explicit title/description means the caller chose its own copy.
  if (title === undefined && description === undefined && isRequesterUnresolvedError(error)) {
    return <RequesterUnresolvedNotice className={cn("w-full", className)} />;
  }

  // A failure logged for IT always reassures (8 Oct): the caller's headline
  // still says what did not go through, but the explanation is ours.
  const heading = title ?? resolved.title;
  const body = resolved.logged ? resolved.description : (description ?? resolved.description);
  const contactLine =
    offerHelpdeskContact && shouldOfferHelpdeskContact(error) ? <HelpdeskContactLine /> : null;

  const retryButton =
    onRetry === undefined ? null : (
      <Button variant="outline" tone="neutral" onClick={onRetry}>
        {t("button.retry")}
      </Button>
    );

  if (variant === "inline") {
    return (
      <Alert tone="danger" title={heading} className={cn("w-full", className)} {...props}>
        <div className="flex flex-col gap-3">
          <span>{body}</span>
          {contactLine}
          {resolved.traceLabel === null ? null : (
            <Typography variant="caption" tone="muted">
              {resolved.traceLabel}
            </Typography>
          )}
          {retryButton === null && actions === undefined ? null : (
            <div className="flex flex-wrap items-center gap-2">
              {retryButton}
              {actions}
            </div>
          )}
        </div>
      </Alert>
    );
  }

  return (
    <div className={cn("w-full", className)} role="alert" {...props}>
      <EmptyState
        icon={<Icon icon={CircleAlert} size="lg" tone="danger" />}
        title={heading}
        description={
          <span className="flex flex-col gap-1">
            <span>{body}</span>
            {contactLine}
            {resolved.traceLabel === null ? null : (
              <Typography variant="caption" tone="muted">
                {resolved.traceLabel}
              </Typography>
            )}
          </span>
        }
        actions={
          retryButton === null && actions === undefined ? undefined : (
            <div className="flex flex-wrap items-center justify-center gap-2">
              {retryButton}
              {actions}
            </div>
          )
        }
      />
    </div>
  );
}
